// src/components/energy-reset/BadgeZoomOverlay.tsx
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Лёгкий FLIP-оверлей:
 * - Когда open === true — рисует «бейдж» в центре и делает лёгкий поп-ин.
 * - Когда open меняется на false — совершает полёт к плитке бейджей и сам скрывается.
 * - Не перехватывает события (pointer-events: none), скролл не блокирует.
 * - Цель:
 *    1) [data-badge-code="CODE"] — если есть такая разметка;
 *    2) #badges-tile button[title^="LABEL"] — падение назад по тексту;
 *    3) #badges-tile — центр блока.
 */
type Props = {
  open: boolean;
  code: string | null;
  label: string | null;
  /** длительность полёта при закрытии, мс */
  flyDurationMs?: number;
  /** длительность поп-апа при появлении, мс */
  popDurationMs?: number;
};

export default function BadgeZoomOverlay({
  open,
  code,
  label,
  flyDurationMs = 620,
  popDurationMs = 240,
}: Props) {
  const [shouldRender, setShouldRender] = useState(open); // держим DOM до окончания полёта
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chipRef = useRef<HTMLDivElement | null>(null);

  // Центр экрана — стартовая позиция
  const start = useMemo(
    () => ({
      x: Math.round(window.innerWidth / 2),
      y: Math.round(window.innerHeight * 0.33), // чуть выше середины экрана
    }),
    []
  );

  // Без DOM — не рендерим
  useEffect(() => {
    if (open) setShouldRender(true);
  }, [open]);

  // Поп-ин при открытии
  useEffect(() => {
    if (!shouldRender || !open || !chipRef.current) return;

    const el = chipRef.current;
    const anim = el.animate(
      [
        { transform: "translate(-50%,-50%) scale(0.85)", opacity: 0 },
        { transform: "translate(-50%,-50%) scale(1.0)", opacity: 1 },
      ],
      { duration: popDurationMs, easing: "cubic-bezier(.22,.61,.36,1)", fill: "forwards" }
    );

    return () => anim.cancel();
  }, [open, shouldRender, popDurationMs]);

  // Вычисление цели: конкретная плитка → кнопка по title → центр блока
  const findTargetRect = (): DOMRect | null => {
    // 1) по коду бейджа
    if (code) {
      const byCode = document.querySelector<HTMLElement>(`[data-badge-code="${cssEscape(code)}"]`);
      if (byCode) return byCode.getBoundingClientRect();
    }
    // 2) по началу title с лейблом
    if (label) {
      const selector = `#badges-tile button[title^="${cssEscape(label)}"]`;
      const byTitle = document.querySelector<HTMLElement>(selector);
      if (byTitle) return byTitle.getBoundingClientRect();
    }
    // 3) центр контейнера
    const tile = document.querySelector<HTMLElement>("#badges-tile");
    return tile ? tile.getBoundingClientRect() : null;
  };

  // Полёт к цели при закрытии
  useEffect(() => {
    if (!shouldRender || open) return; // ждём перехода в false
    const chip = chipRef.current;
    if (!chip) return;

    const rect = findTargetRect();
    if (!rect) {
      // если цели нет — просто плавно убрать
      const fade = chip.animate(
        [
          { transform: "translate(-50%,-50%) scale(1)", opacity: 1 },
          { transform: "translate(-50%,-50%) scale(0.9)", opacity: 0 },
        ],
        { duration: 220, easing: "ease-out", fill: "forwards" }
      );
      fade.onfinish = () => setShouldRender(false);
      return () => fade.cancel();
    }

    const endX = rect.left + rect.width / 2;
    const endY = rect.top + rect.height / 2;

    const dx = endX - start.x;
    const dy = endY - start.y;

    const anim = chip.animate(
      [
        { transform: "translate(-50%,-50%) scale(1)", opacity: 1, offset: 0 },
        {
          transform: `translate(calc(-50% + ${dx * 0.6}px), calc(-50% + ${dy * 0.6}px)) scale(1.05)`,
          opacity: 1,
          offset: 0.65,
        },
        {
          transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.38)`,
          opacity: 0,
          offset: 1,
        },
      ],
      { duration: flyDurationMs, easing: "cubic-bezier(.22,.61,.36,1)", fill: "forwards" }
    );

    anim.onfinish = () => setShouldRender(false);
    return () => anim.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shouldRender, start.x, start.y, flyDurationMs, code, label]);

  if (!shouldRender) return null;

  const caption = label || "Бейдж получен!";

  return (
    <div
      ref={hostRef}
      className="fixed inset-0"
      style={{
        zIndex: 40, // под модалкой (z-50), над контентом
        pointerEvents: "none",
      }}
      aria-hidden
    >
      {/* сам «чип» бейджа */}
      <div
        ref={chipRef}
        style={{
          position: "fixed",
          left: `${start.x}px`,
          top: `${start.y}px`,
          transform: "translate(-50%,-50%)",
          willChange: "transform,opacity",
          filter: "drop-shadow(0 18px 44px rgba(0,0,0,.22))",
        }}
      >
        <div
          style={{
            width: 92,
            height: 92,
            borderRadius: 24,
            background:
              "linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(236, 72, 153, 0.95) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: 40,
            userSelect: "none",
          }}
        >
          🏅
        </div>
        <div
          style={{
            marginTop: 8,
            textAlign: "center",
            fontSize: 13,
            lineHeight: "16px",
            fontWeight: 600,
            color: "rgba(17,24,39,0.9)",
            maxWidth: 240,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={caption}
        >
          {caption}
        </div>
      </div>
    </div>
  );
}

// Небольшой helper для CSS-экранирования (Chrome поддерживает CSS.escape)
function cssEscape(input: string): string {
  if (typeof (window as any).CSS?.escape === "function") {
    // @ts-ignore
    return (window as any).CSS.escape(input);
  }
  // минимальный fallback
  return String(input).replace(/["\\]/g, "\\$&");
}
